import React from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetCopilotCostCentersQuery } from "state/rtkQuery/copilot/copilotQueries";
import { DEFAULT_DELOITTE_OVERAGE_RATE } from "./useDashboardSettings";

const OVERAGE_RED = "#E2231A";
const DELOITTE_ORANGE = "#F28C00";
const UNASSIGNED_GREY_DARK = "#999999";
const UNASSIGNED_GREY_LIGHT = "#cccccc";

const getOverageColor = (isUnassigned) => (isUnassigned ? UNASSIGNED_GREY_DARK : OVERAGE_RED);
const getDeloitteColor = (isUnassigned) => (isUnassigned ? UNASSIGNED_GREY_LIGHT : DELOITTE_ORANGE);

const formatSpend = (v) =>
  "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatSpendCompact = (v) => {
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
};

// total_net_amount IS the overage/billed amount here (list price, before
// Deloitte's negotiated discount) — not the pool-covered portion. Deloitte's
// actual discounted cost isn't returned by this endpoint as its own field
// (unlike ModelTable's per-model rows, which have netOverageCostAmount), so
// it's derived the same way StatCards/ModelTable derive it elsewhere on this
// dashboard: net overage credits × the org's negotiated per-AIC rate.
//
// The bar itself is built as a proper Recharts stack — orange segment first
// (0 to deloitte_cost_amount), red segment for the remainder (continues where
// orange ends, up to total_net_amount) — rather than two independent bars
// both starting at x=0. Two non-stacked <Bar>s don't reliably overlay in
// Recharts' layout model (they lay out side-by-side instead); stacking a
// remainder is the well-supported way to get the same "small orange portion
// within a larger red total" visual.
const withDeloitteCost = (costCenters, deloitteOverageRate) =>
  costCenters.map((row) => {
    const deloitteCost = (row.total_net_credits ?? 0) * deloitteOverageRate;
    return {
      ...row,
      deloitte_cost_amount: deloitteCost,
      overage_remainder_amount: Math.max(0, (row.total_net_amount ?? 0) - deloitteCost),
    };
  });

const CostCenterTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        p: "10px 14px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#555", mb: 0.5 }}>
        {row.cost_center_name}
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: OVERAGE_RED }}>
        {formatSpend(row.total_net_amount)} overage billed
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: DELOITTE_ORANGE }}>
        {formatSpend(row.deloitte_cost_amount)} net overage amount after Deloitte discount
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: "#999", mt: 0.5 }}>
        {row.engineer_count.toLocaleString("en-AU")} engineers
      </Typography>
    </Box>
  );
};

const CostCenterTable = ({ orgId, platformId, fromDate, toDate, orgName, deloitteOverageRate, onSelectCostCenter }) => {
  const { data, isLoading, isFetching } = useGetCopilotCostCentersQuery(
    { orgId, platformId, fromDate, toDate },
    { refetchOnMountOrArgChange: true }
  );

  const effectiveDeloitteRate = deloitteOverageRate ?? DEFAULT_DELOITTE_OVERAGE_RATE;
  const costCenters = withDeloitteCost(data ?? [], effectiveDeloitteRate);
  const totalEngineers = costCenters.reduce((sum, cc) => sum + (cc.engineer_count ?? 0), 0);

  const chartHeight = Math.max(costCenters.length * 46, 180);
  const loading = isLoading || isFetching;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid #e8e8e8",
        backgroundColor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a" }}>
            Consumption by Cost Center{orgName ? ` for the users in ${orgName} ` : ""}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>
            Overage billed vs. what Deloitte actually pays after discount
          </Typography>
        </Box>
        {fromDate && toDate && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.2,
              py: 0.35,
              backgroundColor: "#f7f7f7",
              border: "1px solid #e8e8e8",
              borderRadius: "20px",
            }}
          >
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#777" }}>
              {fromDate} → {toDate}
            </Typography>
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 220 }}>
          <CircularProgress size={32} sx={{ color: OVERAGE_RED }} />
        </Box>
      ) : costCenters.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 160 }}>
          <Typography sx={{ fontSize: "0.82rem", color: "#bbb", fontStyle: "italic" }}>
            No cost center data for this date range.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 2, py: 2 }}>
          {/* Legend */}
          <Box sx={{ display: "flex", gap: 2.5, px: 1, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: OVERAGE_RED }} />
              <Typography sx={{ fontSize: "0.72rem", color: "#555" }}>Overage billed</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: DELOITTE_ORANGE }} />
              <Typography sx={{ fontSize: "0.72rem", color: "#555" }}>Net Overage Amount after Deloitte Discount</Typography>
            </Box>
          </Box>

          <Box sx={{ "& svg, & svg *": { outline: "none" } }}>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={costCenters}
              layout="vertical"
              margin={{ top: 5, right: 70, left: 10, bottom: 0 }}
              barCategoryGap={12}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatSpendCompact}
              />
              <YAxis
                type="category"
                dataKey="cost_center_name"
                tick={{ fontSize: 12, fill: "#555" }}
                axisLine={false}
                tickLine={false}
                width={170}
              />
              <Tooltip content={<CostCenterTooltip />} cursor={{ fill: "#f7f7f7" }} />
              {/* Orange segment — Deloitte's actual discounted cost, drawn from x=0. */}
              <Bar
                dataKey="deloitte_cost_amount"
                name="Net Overage Amount after Deloitte Discount"
                stackId="overage"
                radius={[4, 0, 0, 4]}
                maxBarSize={24}
                // Recharts filters out zero-width/zero-height bar segments
                // entirely — minPointSize forces a sliver of visible width so
                // a $0 Deloitte cost segment still renders instead of vanishing.
                minPointSize={2}
                onClick={(row) => {
                  if (onSelectCostCenter && row.cost_center_id) onSelectCostCenter(row);
                }}
                cursor={onSelectCostCenter ? "pointer" : "default"}
              >
                {costCenters.map((row) => (
                  <Cell
                    key={row.cost_center_id ?? "unassigned"}
                    fill={getDeloitteColor(row.cost_center_id === null)}
                  />
                ))}
                <LabelList
                  dataKey="deloitte_cost_amount"
                  position="insideLeft"
                  formatter={formatSpend}
                  style={{ fontSize: 11, fontWeight: 600, fill: "#fff" }}
                />
              </Bar>
              {/* Red remainder segment — continues where orange ends, up to the
                  full list-price overage (total_net_amount). Labeled at the end
                  of the stack, so the printed number is the true total overage. */}
              <Bar
                dataKey="overage_remainder_amount"
                name="Overage billed"
                stackId="overage"
                radius={[0, 4, 4, 0]}
                maxBarSize={24}
                minPointSize={2}
                onClick={(row) => {
                  if (onSelectCostCenter && row.cost_center_id) onSelectCostCenter(row);
                }}
                cursor={onSelectCostCenter ? "pointer" : "default"}
              >
                {costCenters.map((row) => (
                  <Cell
                    key={row.cost_center_id ?? "unassigned"}
                    fill={getOverageColor(row.cost_center_id === null)}
                  />
                ))}
                <LabelList
                  dataKey="total_net_amount"
                  position="right"
                  formatter={formatSpend}
                  style={{ fontSize: 11, fontWeight: 600, fill: "#1a1a1a" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
          {costCenters.length} cost centers &middot; {totalEngineers.toLocaleString("en-AU")} engineers total
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
          Sorted by spend, descending
        </Typography>
      </Box>
    </Paper>
  );
};

export default CostCenterTable;
